import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { succeeded } from './asyncResult';
import { CredentialForm } from './CredentialForm';
import { shouldShowCredentialForm, shouldShowNotEntitledNotice } from './entitlementGate';
import { useBillAutomationEntitlement } from './useBillAutomationEntitlement';
import { credentialFailureMessage, credentialPurposeCopy, credentialStatusLabel, credentialStatusTooltip, credentialStatusVariant, isPausedCredential, PAUSED_MESSAGE } from './credentialStatus';
import { UpgradeNotice } from './UpgradeNotice';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
import { notifyCredentialsChanged } from './credentialEvents';
export { credentialStatusLabel, credentialStatusVariant, credentialFailureMessage } from './credentialStatus';
export { findCredentialForUtility } from './credentialStatus';
export { CredentialForm } from './CredentialForm';
const formatDate = value => value ? new Date(value).toLocaleDateString() : null;
export const OK_ROTATED = 'Login updated. Livable tries it at the next daily collection, so the status can take a day to change.';
export const OK_DETAILS = 'Details saved.';

/**
 * Credential state + actions for a single property utility.
 *
 * Property managers and staff. The caller decides whether to render it (canManageUtilityCredentials), but that
 * is a convenience, not the control: the API is PmRegularOrHigher and pins a customer to their own company.
 */
export const UtilityPortalCredentialSection = observer(_ref => {
  var _credential$id;
  let companyId = _ref.companyId,
    utilityProviderId = _ref.utilityProviderId,
    propertyId = _ref.propertyId,
    propertyUtilityId = _ref.propertyUtilityId,
    utilityTypeId = _ref.utilityTypeId,
    propertyReference = _ref.propertyReference,
    credential = _ref.credential,
    providerName = _ref.providerName,
    isAutomated = _ref.isAutomated,
    onChanged = _ref.onChanged;
  const store = useStore();
  const _useState = useState(!credential),
    _useState2 = _slicedToArray(_useState, 2),
    isEditing = _useState2[0],
    setIsEditing = _useState2[1];
  // Seeded from a prop that can arrive after mount. saas-react-components v3 keeps modal children
  // mounted while the modal is open, so the remount that used to re-seed this no longer happens
  // and a credential loading late would leave the section stuck on the form. Keyed on the id, not
  // the object, so a refetch returning the same credential does not interrupt an edit in progress.
  const credentialId = credential === null || credential === void 0 ? void 0 : credential.id;
  useEffect(() => setIsEditing(!credentialId), [credentialId]);
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    busy = _useState4[0],
    setBusy = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    saved = _useState8[0],
    setSaved = _useState8[1];
  // Company-scoped: a confirmation for one company must not show after switching to another.
  useEffect(() => setSaved(''), [companyId]);
  const entitled = useBillAutomationEntitlement(companyId);
  const status = credentialStatusLabel(credential);
  const failure = credentialFailureMessage(credential);

  // Takes the model as well as the call: disableUtilityCredential returns void, so the response
  // shape cannot tell success from failure - only the model's error flag can.
  const runAction = async (label, model, action) => {
    setError('');
    setBusy(label);
    try {
      const resp = await action();
      if (!succeeded(model, resp)) {
        setError("Could not ".concat(label.toLowerCase(), " this credential."));
        return;
      }
      if (onChanged) await onChanged();
      notifyCredentialsChanged();
    } catch (ex) {
      setError("Could not ".concat(label.toLowerCase(), " this credential."));
    } finally {
      setBusy('');
    }
  };
  const handleDisable = () => runAction('Disable', store.admin.disableUtilityCredentialAsync, () => store.admin.disableUtilityCredentialAsync.run(credential.id));
  const handleSaved = async function () {
    let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        rotated: true
      },
      rotated = _ref2.rotated;
    setSaved(rotated ? OK_ROTATED : OK_DETAILS);
    setIsEditing(false);
    if (onChanged) await onChanged();
    // After the modal's reload: both use one async model, and the later run supersedes the earlier.
    notifyCredentialsChanged();
  };
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Bill automation"), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginY: '8px'
    }
  }, error) : null, saved ? /*#__PURE__*/React.createElement(Alert, {
    severity: "success",
    sx: {
      marginY: '8px'
    }
  }, saved) : null, credential ? /*#__PURE__*/React.createElement(Stack, {
    spacing: "12px",
    sx: {
      marginY: '12px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    alignItems: "center",
    flexWrap: "wrap"
  }, /*#__PURE__*/React.createElement(Tooltip, {
    title: credentialStatusTooltip(credential, {
      isAutomated
    }),
    placement: "top",
    arrow: true
  }, /*#__PURE__*/React.createElement(Chip, {
    tabIndex: 0,
    size: "small",
    label: status,
    variant: credentialStatusVariant(credential)
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, credential.usernameHint), formatDate(credential.lastVerifiedAt) ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, "Last verified ", formatDate(credential.lastVerifiedAt)) : null), isPausedCredential(credential) ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, PAUSED_MESSAGE) : null, failure ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, failure) : null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    onClick: () => {
      setSaved('');
      setIsEditing(current => !current);
    }
  }, isEditing ? 'Cancel' : 'Update login'), credential.disabledOn ? null : /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    disabled: !!busy,
    onClick: handleDisable
  }, "Disable"))) : null, entitled && !credential ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginY: '8px'
    }
  }, credentialPurposeCopy(providerName, isAutomated)) : null, shouldShowNotEntitledNotice(entitled, credential) ?
  /*#__PURE__*/
  /* Explained with a way forward, not hidden: a form that silently vanishes reads as a bug. */
  React.createElement(UpgradeNotice, {
    isLivableStaff: isLivableStaffFromToken()
  }) : null, isEditing && shouldShowCredentialForm(entitled, credential) ? /*#__PURE__*/React.createElement(CredentialForm
  // A new login or company remounts the form, so nothing typed for one is saved against another.
  , {
    key: "".concat(companyId, ":").concat((_credential$id = credential === null || credential === void 0 ? void 0 : credential.id) !== null && _credential$id !== void 0 ? _credential$id : 'new'),
    companyId: companyId,
    utilityProviderId: utilityProviderId,
    propertyId: propertyId,
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: utilityTypeId,
    propertyReference: propertyReference,
    existing: credential,
    onSaved: handleSaved
  }) : null);
});